import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Star, CheckCircle2 } from 'lucide-react';
import { professionals } from '../data/mockData';
export function RatingScreen() {
  const navigate = useNavigate();
  const professional = professionals[0];
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [review, setReview] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const tags = ['Professional', 'On Time', 'Helpful', 'Clear Communication', 'Would Recommend'];
  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };
  if (submitted) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="flex flex-col items-center justify-center min-h-screen bg-slate-50 p-6 text-center" data-id="element-1463">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-1464">
          <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-1465" />
        </motion.div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-1466">Thank You!</h2>
        <p className="text-slate-500 mb-8 max-w-[250px]" data-id="element-1467">
          Your review helps others find trusted care on Whiz.
        </p>
        <button onClick={() => navigate('/appointments')} className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm hover:bg-blue-700 transition-colors" data-id="element-1468">
          Done
        </button>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="flex flex-col min-h-screen bg-slate-50" data-id="element-1469">
      <div className="bg-white px-6 pt-12 pb-4 shadow-sm sticky top-0 z-10 flex items-center gap-3" data-id="element-1470">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-slate-50 rounded-full" data-id="element-1471">
          <ArrowLeft className="w-6 h-6 text-slate-700" data-id="element-1472" />
        </button>
        <h1 className="text-xl font-bold text-slate-900" data-id="element-1473">Rate Visit</h1>
      </div>

      <div className="p-6 flex-1 overflow-y-auto" data-id="element-1474">
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 mb-8 flex items-center gap-4 text-center flex-col" data-id="element-1475">
          <img src={professional.image} alt={professional.name} className="w-20 h-20 rounded-full object-cover shadow-sm" data-id="element-1476" />
          <div data-id="element-1477">
            <h2 className="font-bold text-slate-900 text-lg" data-id="element-1478">
              {professional.name}
            </h2>
            <p className="text-sm text-slate-500" data-id="element-1479">{professional.specialty}</p>
          </div>
        </div>

        <div className="mb-8 text-center" data-id="element-1480">
          <h3 className="font-semibold text-slate-900 mb-4 text-lg" data-id="element-1481">
            How was your experience?
          </h3>
          <div className="flex justify-center gap-2" data-id="element-1482">
            {[1, 2, 3, 4, 5].map(star => <button key={star} onMouseEnter={() => setHoveredRating(star)} onMouseLeave={() => setHoveredRating(0)} onClick={() => setRating(star)} className="p-1 focus:outline-none transition-transform hover:scale-110" data-id="element-1483">
                <Star className={`w-10 h-10 ${star <= (hoveredRating || rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'} transition-colors`} data-id="element-1484" />
              </button>)}
          </div>
        </div>

        {rating > 0 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-6" data-id="element-1485">
            <div data-id="element-1486">
              <h3 className="font-semibold text-slate-900 mb-3" data-id="element-1487">
                What went well?
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-1488">
                {tags.map(tag => <button key={tag} onClick={() => toggleTag(tag)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${selectedTags.includes(tag) ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-1489">
                    {tag}
                  </button>)}
              </div>
            </div>

            <div data-id="element-1490">
              <h3 className="font-semibold text-slate-900 mb-3" data-id="element-1491">
                Write a review (Optional)
              </h3>
              <textarea value={review} onChange={e => setReview(e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl p-4 text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 min-h-[120px]" placeholder="Share more about your experience..." data-id="element-1492" />
            </div>

            <div className="bg-blue-50 p-4 rounded-xl flex items-start gap-3" data-id="element-1493">
              <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" data-id="element-1494" />
              <p className="text-sm text-blue-800" data-id="element-1495">
                Your review will be posted publicly with a{' '}
                <span className="font-semibold" data-id="element-1496">Verified Patient Review</span>{' '}
                badge.
              </p>
            </div>
          </motion.div>}
      </div>

      <div className="p-6 bg-white border-t border-slate-100" data-id="element-1497">
        <button onClick={() => setSubmitted(true)} disabled={rating === 0} className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1498">
          Submit Review
        </button>
      </div>
    </motion.div>;
}